import { type KeyboardEvent, useRef, useState } from "react";

import styles from "./Board.module.css";
import { ChecksDot } from "./ChecksDot";
import { TaskCard } from "./TaskCard";
import { Avatar } from "../../components/Avatar";
import type { Column, Task } from "../../data/types";
import { Icon } from "../../icons";
import { cx } from "../../lib/cx";
import { isTerminal, statusColorVar } from "../../lib/status";

// The five board columns, in order. Task status drives which column a card
// lands in (there is no drag-and-drop — see agents-and-tasks.md).
const COLUMNS: ReadonlyArray<Column> = [
  { id: "pending", label: "Pending", status: "pending" },
  { id: "running", label: "In Progress", status: "running" },
  { id: "needs-input", label: "Needs Input", status: "needs-input" },
  { id: "review", label: "In Review", status: "review" },
  { id: "done", label: "Done", status: "done" },
];

const EMPTY = new Set<string>();

export type BoardViewProps = {
  readonly archivedIds: ReadonlyArray<string>;
  readonly layout: string;
  readonly onArchive: (id: string) => void;
  readonly onArchiveTask: (id: string) => void;
  readonly onOpen: (task: Task) => void;
  readonly onToggleSel?: (id: string) => void;
  readonly repoNameFor?: (repoId: string) => string;
  readonly selected?: ReadonlySet<string>;
  readonly showLiveFooter?: boolean;
  readonly taskHref: (task: Task) => string;
  readonly tasks: ReadonlyArray<Task>;
};

export const BoardView = ({
  archivedIds,
  layout,
  onArchive,
  onArchiveTask,
  onOpen,
  onToggleSel,
  repoNameFor,
  selected = EMPTY,
  showLiveFooter = false,
  taskHref,
  tasks,
}: BoardViewProps) => {
  // The Done column holds everything terminal, so a failed task still leaves
  // the active columns — it just does not claim to have succeeded once there.
  //
  // Newest first, on creation time: the column has to be a fixed list of
  // places, so that a card only ever moves when its status does. Sorting on
  // activity instead shuffled the column under the pointer every time an agent
  // took a turn, and a click landed on whichever card slid into the gap.
  const byCol = (status: string): ReadonlyArray<Task> =>
    tasks
      .filter((t) =>
        status === "done" ? isTerminal(t.status) : t.status === status,
      )
      .sort((a, b) => b.createdAt - a.createdAt);

  // Roving-tabindex keyboard navigation for the kanban: exactly one card is the
  // tab stop; arrows move within / between columns and Enter/Space opens it.
  const cardRefs = useRef(new Map<string, HTMLAnchorElement>());
  const [activeId, setActiveId] = useState<string | null>(null);
  // Each column's cards, sorted once: the keyboard grid and the rendered
  // columns have to agree on where every card is.
  const columns = COLUMNS.map((c) => ({ ...c, cards: byCol(c.id) }));
  const grid = columns.map((c) => c.cards);
  const firstId = grid.find((cards) => cards.length > 0)?.[0]?.id ?? null;
  const roveId =
    activeId !== null &&
    grid.some((cards) => cards.some((t) => t.id === activeId))
      ? activeId
      : firstId;

  const focusCard = (id: string): void => {
    setActiveId(id);
    cardRefs.current.get(id)?.focus();
  };

  const onBoardKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    // Only handle keys coming from a card; inner buttons keep their own keys.
    if (
      !(e.target as HTMLElement).hasAttribute("data-task-card") ||
      roveId === null
    ) {
      return;
    }
    let ci = -1;
    let ri = -1;
    grid.forEach((cards, i) => {
      const r = cards.findIndex((t) => t.id === roveId);
      if (r >= 0) {
        ci = i;
        ri = r;
      }
    });
    const col = grid[ci];
    if (!col) {
      return;
    }
    const move = (id: string | undefined): void => {
      if (id !== undefined) {
        focusCard(id);
      }
    };
    if (e.key === "ArrowDown") {
      e.preventDefault();
      move(col[Math.min(ri + 1, col.length - 1)]?.id);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      move(col[Math.max(ri - 1, 0)]?.id);
    } else if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
      e.preventDefault();
      const dir = e.key === "ArrowRight" ? 1 : -1;
      for (let j = ci + dir; j >= 0 && j < grid.length; j += dir) {
        const cards = grid[j];
        if (cards && cards.length > 0) {
          move(cards[Math.min(ri, cards.length - 1)]?.id);
          break;
        }
      }
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      const t = col[ri];
      if (t) {
        onOpen(t);
      }
    }
  };

  if (layout === "list") {
    return (
      <div className={styles.tasklist}>
        {columns.map(({ cards, id, label, status }) => {
          if (cards.length === 0) {
            return null;
          }
          return (
            <div className={styles.section} key={id}>
              <div className={styles.sectionHead}>
                <span
                  className={styles.swatch}
                  style={{ background: statusColorVar(status) }}
                />
                {label}
                <span className={styles.sectionCount}>· {cards.length}</span>
              </div>
              {cards.map((t) => (
                <div
                  className={cx(
                    styles.row,
                    selected.has(t.id) && styles.selected,
                  )}
                  key={t.id}
                >
                  <a
                    aria-label={`Open ${t.title}`}
                    className={styles.rowLink}
                    href={taskHref(t)}
                    onClick={(e) => {
                      const selectionClick =
                        e.altKey && !e.metaKey && !e.ctrlKey && !e.shiftKey;
                      if (selectionClick && onToggleSel) {
                        e.preventDefault();
                        onToggleSel(t.id);
                      } else if (
                        e.button === 0 &&
                        !e.altKey &&
                        !e.metaKey &&
                        !e.ctrlKey &&
                        !e.shiftKey
                      ) {
                        e.preventDefault();
                        onOpen(t);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === " ") {
                        e.preventDefault();
                        onOpen(t);
                      }
                    }}
                  />
                  <span
                    className={styles.statusDot}
                    style={{
                      background: statusColorVar(t.status),
                      color: statusColorVar(t.status),
                    }}
                    title={t.status}
                  />
                  <div className={styles.rowMain}>
                    <div className={styles.rowTitle}>{t.title}</div>
                    <div className={styles.rowId}>
                      {t.displayId}
                      {repoNameFor ? ` · ${repoNameFor(t.repo)}` : ""}
                    </div>
                  </div>
                  <div className={styles.branch}>
                    {t.branch ? (
                      <>
                        <Icon.Branch
                          size={11}
                          style={{ marginRight: 3, verticalAlign: -1 }}
                        />
                        {t.branch}
                      </>
                    ) : null}
                  </div>
                  <div className={styles.when}>{t.time}</div>
                  <div className={styles.rowStats}>
                    {t.files > 0 && (
                      <>
                        {t.files} files ·{" "}
                        <span className={styles.statAdd}>+{t.add}</span>{" "}
                        <span className={styles.statDel}>−{t.rem}</span>
                      </>
                    )}
                  </div>
                  <ChecksDot summary={t.checks} />
                  {/* Archiving is one-way — it tears the sandbox down — so an
                      already archived row offers nothing here. The placeholder
                      keeps the grid columns aligned either way. */}
                  {isTerminal(t.status) && !archivedIds.includes(t.id) ? (
                    <button
                      className={styles.archive}
                      onClick={(e) => {
                        e.stopPropagation();
                        onArchive(t.id);
                      }}
                      title="Archive"
                      type="button"
                    >
                      <Icon.Archive size={11} />
                    </button>
                  ) : (
                    <span />
                  )}
                  <Avatar name={t.who} size={20} />
                </div>
              ))}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className={styles.kanban} onKeyDown={onBoardKeyDown}>
      {columns.map(({ cards, id, label, status }) => (
        <div className={styles.col} key={id}>
          <div className={styles.colHead}>
            <div className={styles.colTitle}>
              <span
                className={styles.swatch}
                style={{ background: statusColorVar(status) }}
              />
              {label}
            </div>
            <span className={styles.colCount}>{cards.length}</span>
          </div>
          <div className={styles.colBody}>
            {cards.map((t) => (
              <TaskCard
                archived={archivedIds.includes(t.id)}
                cardRef={(el) => {
                  if (el) {
                    cardRefs.current.set(t.id, el);
                  } else {
                    cardRefs.current.delete(t.id);
                  }
                }}
                href={taskHref(t)}
                key={t.id}
                onArchive={() => onArchive(t.id)}
                onArchiveTask={() => onArchiveTask(t.id)}
                onCardFocus={() => setActiveId(t.id)}
                onOpen={() => onOpen(t)}
                onToggleSel={onToggleSel ? () => onToggleSel(t.id) : undefined}
                selected={selected.has(t.id)}
                showRepo={repoNameFor ? repoNameFor(t.repo) : undefined}
                tabIndex={t.id === roveId ? 0 : -1}
                task={t}
              />
            ))}
            {cards.length === 0 && <div className={styles.colEmpty} />}
            {showLiveFooter && id === "running" && cards.length > 0 ? (
              <div className={styles.colLive}>
                <span className="live-dot" /> live
              </div>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  );
};
